import React, { useState, useEffect, useCallback, useRef } from "react"
import Modal from "aldoo-ra/Modal"
import Tabs from "aldoo-ra/Tabs"
import Typography from "aldoo-ra/Typography"
import PubSub from "pubsub-js"
import PagePreview from "aldoo-ra/HtmlEditor/plugins/page-preview"
import NewsletterAPI from "../api"
import NewsletterRealtimeController from "../realtime-controller"
import TemplateVariablesModal from "../ReleaseGenerator/components/TemplateVariablesModal"
import { useReleaseStats } from "./hooks/useReleaseStats"
import ReleaseActionsController from "./controllers/ReleaseActionsController"
import { showNotification } from "aldoo-ra/Notification"
import ReleaseHeader from "./components/ReleaseHeader"
import TemplatesTab from "./components/TemplatesTab"
import StatisticsTab from "./components/StatisticsTab"
import DetailsTab from "./components/DetailsTab"
import RescheduleModal from "./components/RescheduleModal"

const ReleaseDetails = ({ isOpen, onClose, releaseId, onReleaseUpdated }) => {
  const [release, setRelease] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [activeTab, setActiveTab] = useState("statistics")
  const [previewTemplateId, setPreviewTemplateId] = useState(null)
  const [showVariablesModal, setShowVariablesModal] = useState(false)
  const [selectedTemplateForVariables, setSelectedTemplateForVariables] = useState(null)
  const [serverTimeOffset, setServerTimeOffset] = useState(0)
  const [timeRemaining, setTimeRemaining] = useState(null)
  const [currentTime, setCurrentTime] = useState(new Date())
  const [nextBatchCountdown, setNextBatchCountdown] = useState(null)
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const [showRescheduleModal, setShowRescheduleModal] = useState(false)

  // Ref to hold latest release data for timer without triggering re-renders
  const releaseRef = useRef(release)

  // Keep ref in sync with release state
  useEffect(() => {
    releaseRef.current = release
  }, [release])

  // Load release details
  useEffect(() => {
    if (isOpen && releaseId) {
      loadReleaseDetails()
    }
  }, [isOpen, releaseId])

  const loadReleaseDetails = async (isBackgroundUpdate = false) => {
    try {
      // Only show loading spinner on initial load, not on background updates
      if (!isBackgroundUpdate) {
        setLoading(true)
        setError(null)
      }

      const response = await NewsletterAPI.getRelease({ releaseId })

      if (!response || !response.release) {
        throw new Error('Failed to load release details')
      }

      setRelease(response.release)
    } catch (err) {
      console.error('Failed to load release:', err)
      // Only show error on initial load, silently fail on background updates
      if (!isBackgroundUpdate) {
        setError(err.message || 'Failed to load release details')
      }
    } finally {
      if (!isBackgroundUpdate) {
        setLoading(false)
      }
    }
  }

  // Statistics polling - only update if values actually changed
  const handleStatsUpdate = useCallback((statsResponse) => {
    setRelease(prev => {
      if (!prev) return prev

      // Build updates object with only changed fields
      const updates = {}
      let hasChanges = false

      if (statsResponse.emailsSent !== undefined && statsResponse.emailsSent !== prev.emailsSent) {
        updates.emailsSent = statsResponse.emailsSent
        hasChanges = true
      }

      if (statsResponse.status && statsResponse.status !== prev.status) {
        updates.status = statsResponse.status
        hasChanges = true
      }

      // For objects, check if reference is different (server sends new object when changed)
      if (statsResponse.deliveryStats && statsResponse.deliveryStats !== prev.deliveryStats) {
        // Deep check to avoid unnecessary updates
        const statsChanged = Object.keys(statsResponse.deliveryStats).some(
          key => statsResponse.deliveryStats[key] !== prev.deliveryStats?.[key]
        )
        if (statsChanged) {
          updates.deliveryStats = statsResponse.deliveryStats
          hasChanges = true
        }
      }

      if (statsResponse.templateStats && statsResponse.templateStats !== prev.templateStats) {
        updates.templateStats = statsResponse.templateStats
        hasChanges = true
      }

      if (statsResponse.nextBatchInfo && statsResponse.nextBatchInfo !== prev.nextBatchInfo) {
        updates.nextBatchInfo = statsResponse.nextBatchInfo
        hasChanges = true
      }

      // Return same reference if nothing changed to prevent re-renders
      if (!hasChanges) return prev

      return { ...prev, ...updates }
    })
  }, [])

  useReleaseStats(releaseId, release, handleStatsUpdate, isOpen)

  // Real-time subscription for release status updates
  useEffect(() => {
    if (!isOpen || !releaseId) return

    NewsletterRealtimeController.init()

    const handleStatusUpdate = (msg, data) => {
      if (data.serverTime) {
        const serverTime = new Date(data.serverTime).getTime()
        const clientTime = new Date().getTime()
        setServerTimeOffset(serverTime - clientTime)
      }

      if (data.releaseId?.toString() === releaseId?.toString()) {
        setRelease(prevRelease => {
          if (!prevRelease) return prevRelease

          return {
            ...prevRelease,
            status: data.status || prevRelease.status,
            emailsSent: data.emailsSent !== undefined ? data.emailsSent : prevRelease.emailsSent,
            estimatedMinutesLeft: data.estimatedMinutesLeft,
            nextBatchInfo: data.nextBatchInfo || prevRelease.nextBatchInfo,
            deliveryStats: data.deliveryStats || prevRelease.deliveryStats,
            templateStats: data.templateStats || prevRelease.templateStats
          }
        })
      }
    }

    const handleRateLimited = (msg, data) => {
      if (data.releaseId?.toString() === releaseId?.toString()) {
        // Reload release details to get fresh nextBatchInfo
        loadReleaseDetails(true)
      }
    }

    const handleDeleted = (msg, data) => {
      if (data.releaseId?.toString() === releaseId?.toString()) {
        onClose()
        if (onReleaseUpdated) onReleaseUpdated()
      }
    }

    const tokens = [
      PubSub.subscribe('newsletter:release_status_updated', handleStatusUpdate),
      PubSub.subscribe('newsletter:release_rate_limited', handleRateLimited),
      PubSub.subscribe('newsletter:release_deleted', handleDeleted)
    ]

    return () => {
      tokens.forEach(token => PubSub.unsubscribe(token))
    }
  }, [isOpen, releaseId, onClose, onReleaseUpdated])

  // Timer for countdown and uptime
  useEffect(() => {
    if (!isOpen || !release) return

    const timer = setInterval(() => {
      setCurrentTime(new Date())
      const currentRelease = releaseRef.current

      if (currentRelease?.scheduledAt && currentRelease?.status === 'scheduled') {
        const scheduled = new Date(currentRelease.scheduledAt)
        const now = new Date().getTime() + serverTimeOffset
        const diff = scheduled.getTime() - now
        setTimeRemaining(diff > 0 ? diff : 0)
      }

      if (currentRelease?.status === 'running' && currentRelease?.startedAt) {
        const startTime = new Date(currentRelease.startedAt).getTime()
        const now = new Date().getTime() + serverTimeOffset
        const elapsedMs = now - startTime
        const seconds = Math.floor(elapsedMs / 1000)
        setElapsedSeconds(seconds)
      }

      if (currentRelease?.status === 'running' && currentRelease?.nextBatchInfo?.nextBatchAt) {
        const nextBatchTime = new Date(currentRelease.nextBatchInfo.nextBatchAt).getTime()
        const now = new Date().getTime()
        const secondsRemaining = Math.max(0, Math.floor((nextBatchTime - now) / 1000))
        setNextBatchCountdown(secondsRemaining)
      } else {
        setNextBatchCountdown(null)
      }
    }, 1000)

    return () => clearInterval(timer)
  }, [isOpen, release?.status, release?.startedAt, release?.scheduledAt, serverTimeOffset])

  // Release action handlers
  const handleStartRelease = async () => {
    const result = await ReleaseActionsController.startRelease(releaseId)
    if (result.success) {
      await loadReleaseDetails(true)
      if (onReleaseUpdated) onReleaseUpdated()
    }
  }

  const handlePauseRelease = async () => {
    const result = await ReleaseActionsController.pauseRelease(releaseId)
    if (result.success) {
      await loadReleaseDetails(true)
      if (onReleaseUpdated) onReleaseUpdated()
    }
  }

  const handleResumeRelease = async () => {
    const result = await ReleaseActionsController.resumeRelease(releaseId)
    if (result.success) {
      await loadReleaseDetails(true)
      if (onReleaseUpdated) onReleaseUpdated()
    }
  }

  const handleStopRelease = async () => {
    const result = await ReleaseActionsController.stopRelease(releaseId)
    if (result.success) {
      await loadReleaseDetails(true)
      if (onReleaseUpdated) onReleaseUpdated()
    }
  }

  const handleDeleteRelease = async () => {
    const result = await ReleaseActionsController.deleteRelease(releaseId, release?.name)
    if (result.success) {
      if (onReleaseUpdated) onReleaseUpdated()
      onClose()
    }
  }

  const handleReschedule = () => {
    setShowRescheduleModal(true)
  }

  const handleRescheduleSubmit = async (newScheduledAt) => {
    try {
      const response = await NewsletterAPI.updateRelease(releaseId, {
        scheduledAt: newScheduledAt
      })

      if (response.success) {
        showNotification({
          message: 'Release rescheduled successfully',
          className: 'bg-admin_success text-white'
        })
        await loadReleaseDetails(true)
        if (onReleaseUpdated) onReleaseUpdated()
        setShowRescheduleModal(false)
      } else {
        throw new Error(response.message || 'Failed to reschedule release')
      }
    } catch (error) {
      throw error
    }
  }

  const handleTemplatePreview = async (templateId) => {
    setPreviewTemplateId(templateId)
  }

  const handleEditVariables = (template) => {
    setSelectedTemplateForVariables(template)
    setShowVariablesModal(true)
  }

  const handleVariablesSaved = () => {
    setShowVariablesModal(false)
    loadReleaseDetails(true)
  }

  const tabs = [
    { value: "statistics", label: "Statistics" },
    { value: "templates", label: "Templates" },
    { value: "details", label: "Details" }
  ]

  if (!isOpen) return null

  // Render header toolbar with status badge and actions
  const renderHeaderToolbar = () => {
    if (loading || !release) return null

    return (
      <ReleaseHeader
        release={release}
        loading={loading}
        onStart={handleStartRelease}
        onPause={handlePauseRelease}
        onResume={handleResumeRelease}
        onStop={handleStopRelease}
        onDelete={handleDeleteRelease}
        onReschedule={handleReschedule}
      />
    )
  }

  const renderFooter = () => {
    if (loading || !release) return null

    const formatTimeRemaining = (ms) => {
      if (!ms || ms <= 0) return null

      const totalSeconds = Math.floor(ms / 1000)
      const days = Math.floor(totalSeconds / 86400)
      const hours = Math.floor((totalSeconds % 86400) / 3600)
      const minutes = Math.floor((totalSeconds % 3600) / 60)
      const seconds = totalSeconds % 60

      if (days > 0) {
        return `${days}d ${hours}h ${minutes}min ${seconds}sec`
      }
      if (hours > 0) {
        return `${hours}h ${minutes}min ${seconds}sec`
      }
      if (minutes > 0) {
        return `${minutes}min ${seconds}sec`
      }
      return `${seconds}sec`
    }

    const timeRemainingText = release.status === 'scheduled' && timeRemaining ? formatTimeRemaining(timeRemaining) : null

    if (!timeRemainingText) return null

    return (
      <div className="flex items-center justify-center py-3">
        <Typography variant="admin_body" className="text-admin_text_secondary">
          Release starts in {timeRemainingText}
        </Typography>
      </div>
    )
  }

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={release?.name || "Release Details"}
        variant="admin_centered"
        className="max-w-4xl"
        showCloseButton={true}
        headerContent={renderHeaderToolbar()}
        footerContent={renderFooter()}
      >
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-admin_primary mx-auto mb-4" />
              <p className="text-admin_text_secondary">Loading release details...</p>
            </div>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-admin_error">{error}</p>
          </div>
        ) : !release ? (
          <div className="text-center py-12">
            <p className="text-admin_text_secondary">No release data available</p>
          </div>
        ) : (
          <div>
            <Tabs
              tabs={tabs}
              value={activeTab}
              onChange={setActiveTab}
              variant="admin"
            />

            <div className="mt-6">
              {activeTab === "statistics" && (
                <StatisticsTab
                  release={release}
                  timeRemaining={timeRemaining}
                  nextBatchCountdown={nextBatchCountdown}
                  serverTimeOffset={serverTimeOffset}
                  elapsedSeconds={elapsedSeconds}
                />
              )}

              {activeTab === "templates" && (
                <TemplatesTab
                  release={release}
                  onPreview={handleTemplatePreview}
                  onEditVariables={handleEditVariables}
                />
              )}

              {activeTab === "details" && (
                <DetailsTab release={release} />
              )}
            </div>
          </div>
        )}
      </Modal>

      {previewTemplateId && (
        <PagePreview
          url={`/template/${previewTemplateId}/preview`}
          isOpen={!!previewTemplateId}
          onClose={() => setPreviewTemplateId(null)}
        />
      )}

      {showVariablesModal && selectedTemplateForVariables && (
        <TemplateVariablesModal
          isOpen={showVariablesModal}
          onClose={() => setShowVariablesModal(false)}
          releaseId={releaseId}
          template={selectedTemplateForVariables}
          onSave={handleVariablesSaved}
        />
      )}

      <RescheduleModal
        isOpen={showRescheduleModal}
        onClose={() => setShowRescheduleModal(false)}
        release={release}
        onReschedule={handleRescheduleSubmit}
      />
    </>
  )
}

export default ReleaseDetails
